/**
 * Add a client.
 *
 * Two interesting parts.
 *
 * **The failure handling.** Three refusals are *expected* here and each is a
 * different sentence:
 *
 * - `VALIDATION_ERROR` — the GSTIN is malformed, its checksum fails, or the
 *   PAN contradicts the one inside the GSTIN. The user's typo, three ways.
 * - `CONFLICT` — this firm already has that GSTIN. Their own record, safe to name.
 * - `USAGE_LIMIT_EXCEEDED` — the plan's client cap. **Not** an error; a
 *   commercial fact, and the screen must say so with a way forward
 *   (APP_DEVELOPMENT_GUIDE §7.3: render `SEAT_LIMIT_EXCEEDED` as "5 of 5 seats
 *   in use", not as "something went wrong").
 *
 * **The GSTIN check (FR-C13).** State code drives place of supply, the legal
 * name is printed on the workbook a client approves — typed by hand, each is a
 * transcription error waiting to decide someone's tax.
 *
 * So the check reads what the GSTIN itself encodes (state, PAN, entity type)
 * and, when ADR-0004's gate opens, what the register says on top. **The two are
 * labelled separately and must stay that way**: "Proprietorship, read from your
 * GSTIN" and "Proprietorship, per the register" are different claims, and a
 * screen that merges them presents the first as the second.
 *
 * Everything it fills stays editable. The register suggests; the firm decides
 * who they act for.
 *
 * The dialog is the shell's `<Modal>` (constitution §7): overlay, titled
 * header, Escape and outside-click to dismiss — the behaviour every other
 * platform dialog has, rather than a third re-implementation of it.
 */

import { Modal } from '@fiveox/shell'
import { Alert, Button, Input } from '@fiveox/ui'
import { ExternalLink, ScanLine } from 'lucide-react'
import { useState } from 'react'

import { ApiError, type TaxpayerLookup, clients } from '../lib/api'
import { STORE_URL } from '../lib/config'

/**
 * Structural check, mirroring the service's `GSTIN_RE` **exactly**.
 *
 * The fourteenth character is `[0-9A-Z]`, not a literal `Z`. It is `Z` on
 * almost every registration, which is exactly why pinning it here was wrong
 * and invisible: this dialog refused `30AADCE8469R1CB` — a real,
 * checksum-valid registration — by disabling its own submit button, while the
 * service would have accepted it. The legacy made the same mistake against the
 * same GSTIN, which is how our own model comment came to name it.
 *
 * Shape only. The mod-36 checksum is the service's to enforce, and whether the
 * GSTIN belongs to anyone is the register's question.
 */
const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z][0-9A-Z][0-9A-Z]$/

/**
 * The GST portal's own public taxpayer search.
 *
 * **A link, not a call.** The practitioner opens it in their own browser, reads
 * the registered name and status, and types what they find. That is a person
 * doing their job; driving the portal from our server — headless browser,
 * CAPTCHA relayed to the user — is the predecessor's mechanism, and it is
 * refused on purpose (see `app/taxpayer/port.py`).
 *
 * It cannot be pre-filled: the portal's search sits behind its own CAPTCHA.
 */
const GST_PORTAL_SEARCH = 'https://services.gst.gov.in/services/searchtp'

const REGISTRATION_TYPES = [
  ['regular', 'Regular'],
  ['composition', 'Composition'],
  ['casual', 'Casual taxable person'],
  ['isd', 'Input service distributor'],
  ['sez', 'SEZ unit / developer'],
  ['non_resident', 'Non-resident taxable person'],
] as const

/**
 * The register's word for a registration type, matched against ours — and
 * `null` when it matches nothing. A near-miss must NOT fall back to
 * `regular`: that is the one value whose wrongness silently changes a return,
 * and a composition dealer filed as regular is not a cosmetic error.
 *
 * Only ever fed from the REGISTER. A GSTIN does not encode registration type,
 * so there is nothing here for the derived half to call.
 */
function knownRegistrationType(reported: string | null): string | null {
  if (!reported) return null
  const wanted = reported.trim().toLowerCase()
  const hit = REGISTRATION_TYPES.find(
    ([value, label]) => value === wanted || label.toLowerCase() === wanted,
  )
  return hit ? hit[0] : null
}

export function NewClientDialog({
  onClose,
  onCreated,
  lookupAvailable = false,
}: {
  onClose: () => void
  onCreated: () => void
  lookupAvailable?: boolean
}) {
  const [legalName, setLegalName] = useState('')
  const [tradeName, setTradeName] = useState('')
  const [gstin, setGstin] = useState('')
  const [constitution, setConstitution] = useState('')
  const [registrationType, setRegistrationType] = useState('regular')
  const [error, setError] = useState<{ message: string; atCap: boolean } | null>(null)
  const [saving, setSaving] = useState(false)

  const [found, setFound] = useState<TaxpayerLookup | null>(null)
  const [checking, setChecking] = useState(false)
  const [checkNote, setCheckNote] = useState<string | null>(null)

  const normalised = gstin.trim().toUpperCase()
  const gstinLooksRight = GSTIN_RE.test(normalised)

  const check = async () => {
    setChecking(true)
    setCheckNote(null)
    setFound(null)
    try {
      const result = (await clients.lookup(normalised)).data
      setFound(result)

      // Filled, not forced. A field the answer leaves blank stays as the user
      // had it — a "prefill" that overwrites a typed name with an empty one is
      // the worst available reading of the word.
      if (result.derived.constitution) setConstitution(result.derived.constitution)

      const register = result.register
      if (register) {
        // The register outranks the derivation wherever it speaks: it is the
        // finer answer ("Private Limited Company" over "Company") and it is
        // the authority the derivation was standing in for.
        if (register.legal_name) setLegalName(register.legal_name)
        if (register.trade_name) setTradeName(register.trade_name)
        if (register.constitution) setConstitution(register.constitution)
        const type = knownRegistrationType(register.registration_type)
        if (type) setRegistrationType(type)
        else if (register.registration_type) {
          setCheckNote(
            `The register reports the registration type as "${register.registration_type}", ` +
              'which is not one of the options here — choose it yourself.',
          )
        }
      }
    } catch (err) {
      setCheckNote(err instanceof ApiError ? err.message : 'The GSTIN could not be checked.')
    } finally {
      setChecking(false)
    }
  }

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setSaving(true)
    setError(null)
    try {
      await clients.create({
        legal_name: legalName.trim(),
        gstin: normalised,
        trade_name: tradeName.trim() || undefined,
        constitution: constitution.trim() || undefined,
        registration_type: registrationType,
      })
      onCreated()
    } catch (err) {
      if (err instanceof ApiError) {
        setError({ message: err.message, atCap: err.code === 'USAGE_LIMIT_EXCEEDED' })
      } else {
        setError({ message: 'The client could not be added.', atCap: false })
      }
    } finally {
      setSaving(false)
    }
  }

  const derived = found?.derived
  const readFacts = derived
    ? [
        `State ${derived.state_code}`,
        `PAN ${derived.pan}`,
        derived.entity_type,
      ].filter(Boolean)
    : []

  return (
    <Modal open onClose={onClose} title="Add a client">
      <form onSubmit={submit} className="space-y-4">
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Input
              label="GSTIN"
              value={gstin}
              onChange={(e) => setGstin(e.target.value)}
              required
              maxLength={15}
              placeholder="27AAAPA1234A1Z5"
              className="font-mono uppercase"
              hint={
                normalised.length === 15 && !gstinLooksRight
                  ? 'That does not look like a valid GSTIN.'
                  : undefined
              }
            />
          </div>
          {lookupAvailable && (
            <Button
              type="button"
              variant="outline"
              className="h-11 shrink-0"
              disabled={!gstinLooksRight}
              loading={checking}
              loadingText="Checking…"
              onClick={() => void check()}
            >
              <ScanLine size={16} /> Check GSTIN
            </Button>
          )}
        </div>

        {found && (
          <Alert tone="info">
            <p>
              <strong>Read from the GSTIN itself:</strong> {readFacts.join(' · ')}.
            </p>
            {derived?.is_additional_registration && (
              <p className="mt-1 text-sm">
                This PAN holds more than one registration in state {derived.state_code}.
              </p>
            )}
            {derived && !derived.is_ordinary_taxpayer && (
              <p className="mt-1 text-sm">
                The fourteenth character is not <code>Z</code> — this is not an ordinary
                taxpayer registration. Check the type before filing for it.
              </p>
            )}
            {found.also_filed_for_this_pan.length > 0 && (
              <p className="mt-1 text-sm">
                You already file for this taxpayer:{' '}
                {found.also_filed_for_this_pan
                  .map((c) => `${c.legal_name} (${c.gstin})`)
                  .join(', ')}
                .
              </p>
            )}
          </Alert>
        )}

        {found?.register && (
          <Alert tone={found.register.registration_status === 'Active' ? 'info' : 'warning'}>
            <p>
              <strong>The register reports</strong> this GSTIN as{' '}
              {found.register.registration_status ?? 'of unknown status'}
              {found.register.registered_on && `, registered ${found.register.registered_on}`}.
            </p>
          </Alert>
        )}

        {found?.register_gate && (
          <Alert tone="warning">
            <p>
              The registered name, status and address are not inside a GSTIN.{' '}
              {found.register_gate}
            </p>
            <a
              href={GST_PORTAL_SEARCH}
              target="_blank"
              rel="noreferrer noopener"
              className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-primary"
            >
              Look this GSTIN up on the GST portal <ExternalLink size={14} />
            </a>
          </Alert>
        )}

        {checkNote && <Alert tone="warning">{checkNote}</Alert>}

        <Input
          label="Legal name"
          value={legalName}
          onChange={(e) => setLegalName(e.target.value)}
          required
          maxLength={200}
        />

        <Input
          label="Trade name"
          value={tradeName}
          onChange={(e) => setTradeName(e.target.value)}
          maxLength={200}
          hint="Optional — the name the business trades under, where it differs."
        />

        <Input
          label="Constitution"
          value={constitution}
          onChange={(e) => setConstitution(e.target.value)}
          maxLength={64}
          hint={
            derived?.constitution
              ? `Read from the GSTIN's PAN. Change it if the register says finer.`
              : 'Optional — proprietorship, partnership, private limited…'
          }
        />

        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-foreground">
            Registration type
          </span>
          <select
            value={registrationType}
            onChange={(e) => setRegistrationType(e.target.value)}
            className="gst-select h-11 w-full px-4"
          >
            {REGISTRATION_TYPES.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          {/* Said out loud, because this is the field the check CANNOT fill and
              the one whose wrongness changes a return. A GSTIN does not encode
              it, and nothing here should let a user assume it was filled in. */}
          <span className="mt-1.5 block text-xs text-muted-foreground">
            Not in the GSTIN — set this from the client's registration certificate.
          </span>
        </label>

        {error && (
          <Alert tone={error.atCap ? 'warning' : 'error'}>
            <p>{error.message}</p>
            {error.atCap && (
              <a
                href={`${STORE_URL}/catalog/gst`}
                className="mt-2 inline-block text-sm font-medium text-primary"
              >
                See plans with more clients →
              </a>
            )}
          </Alert>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={!gstinLooksRight || !legalName.trim()}
            loading={saving}
            loadingText="Adding…"
          >
            Add client
          </Button>
        </div>
      </form>
    </Modal>
  )
}
